import { X } from "lucide-react";

interface SelectionToolbarProps {
    /** How many rows the user has selected. */
    count: number;
    /** Total matching the current filters, used for the "all N matching" hint. */
    total?: number;
    /** When true, the toolbar's actions target the entire filtered set, not just the selected IDs. */
    allMatching?: boolean;
    /** Clears the selection entirely. */
    onClear: () => void;
    /** Right-side action buttons (rendered as-is). */
    children: React.ReactNode;
    /** Optional left-side label override. */
    label?: string;
}

/**
 * Floating bottom-centered bar that appears when one or more rows are
 * selected in a data table. Uses our spring easing curve for entrance so
 * it feels responsive without being toy-like. Intended to be reused across
 * every admin surface that grows multi-select (IoCs, rules, tenants, etc.).
 */
export function SelectionToolbar({
    count,
    total,
    allMatching,
    onClear,
    children,
    label,
}: SelectionToolbarProps): React.ReactElement | null {
    if (count === 0) return null;

    const effective = allMatching && total !== undefined ? total : count;
    const description =
        label ??
        (allMatching && total !== undefined
            ? `${effective.toLocaleString()} matching selected`
            : `${effective.toLocaleString()} selected`);

    return (
        <div
            role="toolbar"
            aria-label="Bulk actions"
            className="enter-up"
            style={{
                position: "fixed",
                bottom: "24px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "var(--color-surface-raised)",
                border: "var(--border-hairline) solid var(--color-border-strong)",
                borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-lg)",
                padding: "var(--space-2) var(--space-3)",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-3)",
                zIndex: "var(--z-toast)" as unknown as number,
                minWidth: 420,
                maxWidth: "calc(100vw - 48px)",
            }}
        >
            <button
                type="button"
                onClick={onClear}
                aria-label="Clear selection"
                className="btn btn--ghost btn--sm press-crush"
                style={{ flexShrink: 0 }}
            >
                <X size={14} />
            </button>
            <span
                className="text-13"
                style={{
                    color: "var(--color-text)",
                    fontWeight: "var(--font-weight-medium)",
                    whiteSpace: "nowrap",
                }}
            >
                {description}
            </span>
            <div
                style={{
                    width: 1,
                    alignSelf: "stretch",
                    background: "var(--color-border)",
                }}
                aria-hidden
            />
            <div className="cluster-xs" style={{ flexWrap: "wrap" }}>
                {children}
            </div>
        </div>
    );
}
